import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { removeOldestQuery } from "@tanstack/react-query-persist-client";
import type { PersistedClient, Persister } from "@tanstack/react-query-persist-client";

// React Query cache persistence — the storage half. App.tsx wires these into
// PersistQueryClientProvider; they live here so they can be unit-tested
// without rendering the whole app (review 2026-10-13).

/** localStorage key of the saved query cache. lib/auth.ts removes it on sign-out. */
export const QUERY_CACHE_KEY = "trybeup_query_cache";

/** How long a saved cache may be restored. queryClient's default gcTime must be
 *  at least this long, or a query nobody is watching is garbage-collected out
 *  of memory and the next save drops it from the blob long before maxAge. */
export const QUERY_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** GoTrue's storageKey (lib/auth.ts). The saved session JSON carries `user.id`. */
const SESSION_KEY = "trybeup_session";

/** Query keys that are never written to disk: volatile, auth/subscription, or
 *  (admin) other people's data that must not sit in localStorage. */
export const PERSIST_SKIP_PREFIXES = [
  "/api/auth/",
  "/api/subscription",
  "/api/notifications/unread",
  // /api/workouts is the most volatile list in the app — a
  // freshly-started workout that's in the cache as "the active
  // one" can flicker against a fresh server response that has
  // a different one (founder report 2026-06-22). Refetch on
  // every boot is cheap; the persistence speed-up doesn't help
  // for a list that's wrong half the time.
  "/api/workouts",
  // Admin screens hold OTHER people's data — user lists, emails, error logs.
  // That must never be left in an admin's localStorage (review 2026-10-13).
  "/api/admin/",
  // The few admin screens keyed by name, not URL (admin-trybes / -duels / -stats).
  "admin-",
];

export function shouldPersistQuery(query: {
  queryKey: readonly unknown[];
  state: { status: string };
}): boolean {
  if (query.state.status !== "success") return false;
  const key = String(query.queryKey[0] ?? "");
  return !PERSIST_SKIP_PREFIXES.some((p) => key.startsWith(p));
}

/** The user id inside a saved GoTrue session string, or null. */
export function sessionUserId(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { user?: { id?: unknown } } | null;
    const id = parsed?.user?.id;
    return typeof id === "string" && id ? id : null;
  } catch {
    return null;
  }
}

function storedSessionUserId(storage: Storage): string | null {
  try {
    return sessionUserId(storage.getItem(SESSION_KEY));
  } catch {
    return null;
  }
}

/**
 * Serialize the cache stamped with the account it belongs to. No signed-in
 * user → "" (nothing saved), so a write that lands after the session is gone
 * can't leave an ownerless blob behind.
 *
 * Defensive replacer: persisting the query cache is the one place the app
 * JSON.stringify's app-wide data on a throttle — if a non-serializable value
 * (a DOM node, a function, a circular ref) ever lands in a cached query, the
 * default JSON.stringify throws "Converting circular structure to JSON"
 * uncaught, surfacing as a TypeError on whatever route the user is on (Sentry
 * TRYBEUP-CAPACITOR, /profile, 2026-07-10, object constructor
 * HTMLAnchorElement). The replacer drops DOM nodes / functions so the common
 * case serializes cleanly; the try/catch skips the persist cycle rather than
 * crash if anything else is non-serializable.
 */
export function serializeForUser(client: PersistedClient, userId: string | null): string {
  if (!userId) return "";
  try {
    return JSON.stringify({ ...client, ownerId: userId }, (_key, value) => {
      if (typeof value === "function") return undefined;
      if (typeof Node !== "undefined" && value instanceof Node) return undefined;
      if (typeof Window !== "undefined" && value instanceof Window) return undefined;
      return value;
    });
  } catch {
    // Non-serializable value slipped in — skip persisting this cycle. An
    // empty string reads back as "no cache" and self-heals.
    return "";
  }
}

/**
 * Whose data is in the in-memory query cache right now (review 2026-10-14).
 *
 * The save stamp is read from the session on disk at write time, which is right
 * for the normal case but wrong when one account replaces another without a
 * sign-out (a magic link / confirmation link opened in a browser where someone
 * else is signed in): A's cache is restored on boot, B's session lands, and the
 * next save would stamp A's leftover queries with B's id. Tracking the owner of
 * what's in memory lets useAuth clear it on the switch, and lets a save refuse
 * to stamp data that belongs to someone else.
 */
let memoryOwner: string | null = null;

/**
 * Tell the cache which account is signed in now. Returns true when memory holds
 * ANOTHER account's data — the caller must clear the query cache. Idempotent.
 */
export function claimCacheForUser(userId: string | null | undefined): boolean {
  if (!userId) return false;
  const switched = memoryOwner !== null && memoryOwner !== userId;
  memoryOwner = userId;
  return switched;
}

/** Signed out: memory no longer belongs to anyone. */
export function forgetCacheOwner(): void {
  memoryOwner = null;
}

/** Test-only peek. */
export function _cacheOwnerForTests(): string | null {
  return memoryOwner;
}

/** The stamp for a save: the session's user, unless memory holds someone else's data. */
export function ownerForSave(sessionUser: string | null): string | null {
  if (!sessionUser) return null;
  if (memoryOwner !== null && memoryOwner !== sessionUser) return null;
  memoryOwner = sessionUser;
  return sessionUser;
}

/** The saved cache, only if it was written for `userId`. */
export function deserializeForUser(raw: string, userId: string | null): PersistedClient | undefined {
  if (!userId) return undefined;
  try {
    const parsed = JSON.parse(raw) as (PersistedClient & { ownerId?: unknown }) | null;
    if (!parsed || parsed.ownerId !== userId) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

/**
 * A persister whose blob is bound to the signed-in account (review 2026-10-13).
 *
 * Sign-out removes the blob, but there are ways around that — a throttled save
 * landing after the removal, a session lost without the sign-out path running,
 * an app killed mid-sign-out. Binding the blob to the session's user id closes
 * them all at once: every save stamps the current session's user id, and a
 * restore only hydrates when that id matches the session on disk now. User B
 * can never first-paint user A's habits, profile or messages.
 */
export function createUserBoundPersister(storage: Storage | undefined): Persister {
  const inner = createSyncStoragePersister({
    storage,
    key: QUERY_CACHE_KEY,
    // Storage full → drop the oldest queries and try again, instead of
    // silently never saving again (review 2026-10-14: gcTime is now 24h, so
    // the blob is much bigger than it used to be).
    retry: removeOldestQuery,
    // Read the owner at WRITE time (the throttle fires up to 1s after the
    // change), so the stamp matches whoever is signed in when it lands.
    serialize: (client) => serializeForUser(client, ownerForSave(storage ? storedSessionUserId(storage) : null)),
  });
  return {
    persistClient: inner.persistClient,
    removeClient: inner.removeClient,
    restoreClient: () => {
      if (!storage) return undefined;
      let raw: string | null = null;
      try {
        raw = storage.getItem(QUERY_CACHE_KEY);
      } catch {
        return undefined;
      }
      if (!raw) return undefined;
      const client = deserializeForUser(raw, storedSessionUserId(storage));
      if (!client) {
        // Someone else's (or an unreadable / ownerless) cache: never paint it.
        try { storage.removeItem(QUERY_CACHE_KEY); } catch { /* ignore */ }
        return undefined;
      }
      memoryOwner = (client as PersistedClient & { ownerId?: string }).ownerId ?? null;
      return client;
    },
  };
}
